/**
 * **Settings** (`/settings`, W28, FR-65, plan D85, D87) — the hub, laid out as
 * ProDesk's (`repo:prodesk:apps/web/src/routes/Firm/settings/Hub.tsx` and
 * `cards.tsx`): the areas as cards in groups, each an icon, a title, what its
 * page is for and **one line of what is set now**, opening its own page.
 * Nothing is edited here.
 *
 * **One read** — `GET /settings`, and the parse profiles for the Parsing
 * card's count — and every line computed from it by a pure function below,
 * never a second source. While it is read a card's line is a skeleton; a read
 * that failed leaves the card its description and no line (`HubCard` says
 * *Open to see what’s set*), never a stale or invented one, and the failure is
 * said once above the groups with a way to try again. *Parsing usage* and
 * *Personal-data erasure* read nothing here: their line is a quiet sentence of
 * what the page holds, not of what is set.
 *
 * Who sees what follows the sections of before: every card for a reader of the
 * settings (`settings:can_view`, the route's own gate) but *Parsing usage*,
 * the administrator's (`settings:can_update`, engine C-E11), and erasure
 * (`mayErase`, D25); a reader without `can_update` is told *View only*.
 *
 * *Managed in FiveOX* closes the hub (FR-37): the organisation, its people and
 * its messaging are the console's — rows out to it, never a copy.
 *
 * **The old addresses land** (D87): `/settings#retention`, as Go To and notes
 * wrote it before W28, is sent on to `/settings/retention`, replacing the
 * entry so Back does not bounce through it.
 */
import { SETTINGS, fourEyesOf, mayErase, sharingOf, useLoad, type SettingsAnswer, type Verb } from '@fiveox/prodoc-ui'
import { Alert, Badge, Button, PageHeader } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import { Building2, ChartColumn, ClipboardCheck, FileType, Hourglass, MessageSquare, ScanText, Share2, UserX, Users } from 'lucide-react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { HubCard, HubGroup, HubRow, type SummaryLine } from '../../components/settings-parts'
import { parsingOf, readableProfiles, type ProfileList } from '../../lib/parsing'
import { useSettingsHost } from './host'
import { orgRetentionRules } from './settings-data'

/* ------------------------------------------------------------ the lines */

const counted = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** *13 in use · 2 your own* — the types a person may pick, and how many are the organisation’s own codes rather than shipped ones. */
export function typesLine(settings: SettingsAnswer): SummaryLine {
  const types = settings.effective.document_types ?? []
  const shipped = new Set((settings.defaults.document_types ?? []).map((t) => t.code))
  const inUse = types.filter((t) => t.enabled).length
  // An answer without the shipped list calls nothing the organisation's own rather than everything.
  const own = shipped.size ? types.filter((t) => !shipped.has(t.code)).length : 0
  return { line: own ? `${inUse} in use · ${own} your own` : `${inUse} in use`, tone: 'set' }
}

/** *Four-eyes off · 4 types go to review* — the switch through the package’s `fourEyesOf` (its default the package’s), and the types in use reviewed on upload. */
export function reviewLine(settings: SettingsAnswer): SummaryLine {
  const defaults = settings.effective.review?.defaults ?? {}
  const reviewed = (settings.effective.document_types ?? []).filter((t) => t.enabled && defaults[t.code]).length
  const eyes = fourEyesOf(settings) ? 'Four-eyes on' : 'Four-eyes off'
  return { line: `${eyes} · ${reviewed ? `${counted(reviewed, 'type')} ${reviewed === 1 ? 'goes' : 'go'} to review` : 'no type goes to review'}`, tone: 'set' }
}

/** *3 rules above the law*, or *The law’s minimums only* when the organisation has no rule of its own — the shipped floors are the law’s, not its rules. */
export function retentionLine(settings: SettingsAnswer): SummaryLine {
  const rules = orgRetentionRules(settings).length
  return rules ? { line: `${counted(rules, 'rule')} above the law`, tone: 'set' } : { line: 'The law’s minimums only', tone: 'unset' }
}

/** *New links last 7 days · passcode optional* — the shipped default where the service holds none. */
export function sharingLine(settings: SettingsAnswer): SummaryLine {
  const sharing = sharingOf(settings)
  return { line: `New links last ${counted(sharing.link_expiry_days, 'day')} · passcode ${sharing.link_passcode_required ? 'required' : 'optional'}`, tone: 'set' }
}

/**
 * *Offered · 9 types read · AI outside India allowed* — whether ProDoc’s
 * screens offer a parse, how many document types are read (the plumbing’s own
 * profiles never counted, a type switched off not read), and where AI may run.
 * `profiles` is `null` when they could not be listed: the count is left out,
 * never guessed.
 */
export function parsingLine(settings: SettingsAnswer, profiles: ProfileList | null): SummaryLine {
  const parsing = parsingOf(settings)
  const parts = [parsing.offered ? 'Offered' : 'Not offered']
  if (profiles) {
    const off = new Set(parsing.profiles_off)
    const read = readableProfiles(profiles.items, parsing.profiles_off).filter((p) => !off.has(p.name)).length
    parts.push(read ? `${counted(read, 'type')} read` : 'no type read yet')
  }
  parts.push(parsing.ai_processing === 'india_only' ? 'India only' : 'AI outside India allowed')
  return { line: parts.join(' · '), tone: 'set' }
}

/* ------------------------------------------------------------ the cards */

type HubGroupKey = 'documents' | 'reading' | 'privacy'

export const HUB_GROUPS: { key: HubGroupKey; title: string; description: string }[] = [
  { key: 'documents', title: 'Documents', description: 'What a document may be called, which wait for a reviewer, and how long each is kept.' },
  { key: 'reading', title: 'Sharing and reading', description: 'How a document leaves the organisation by link, and how ProDoc reads its details.' },
  { key: 'privacy', title: 'Privacy', description: 'A person’s request to have their documents erased.' },
]

/** What a card’s line may read from: each `null` while it is read or when its read failed. */
export interface HubData {
  settings: SettingsAnswer | null
  profiles: ProfileList | null
}

type Read = keyof HubData

export interface SettingsCard {
  key: string
  group: HubGroupKey
  title: string
  /** What the page is for — under the title. */
  description: string
  href: string
  icon: LucideIcon
  /** The reads the line waits for; none for a card whose line is not read. */
  reads: Read[]
  line: (data: HubData) => SummaryLine | null
  /** Absent means every reader of the settings. */
  visible?: (can: (resource: string, verb: Verb) => boolean) => boolean
}

const fromSettings =
  (line: (settings: SettingsAnswer) => SummaryLine) =>
  ({ settings }: HubData): SummaryLine | null =>
    settings ? line(settings) : null

/** Every settings page has one card here, in the order the hub draws them; the hub draws from nothing else. */
export const SETTINGS_CARDS: SettingsCard[] = [
  {
    key: 'types',
    group: 'documents',
    title: 'Document types',
    description: 'What a person may call a document when they upload or edit it. Never guessed from the file.',
    href: '/settings/types',
    icon: FileType,
    reads: ['settings'],
    line: fromSettings(typesLine),
  },
  {
    key: 'review',
    group: 'documents',
    title: 'Review',
    description: 'Which uploads wait for a reviewer, and whether the uploader may approve their own.',
    href: '/settings/review',
    icon: ClipboardCheck,
    reads: ['settings'],
    line: fromSettings(reviewLine),
  },
  {
    key: 'retention',
    group: 'documents',
    title: 'Retention',
    description: 'How long documents are kept. The law sets a minimum for each type; the organisation may keep them longer, never shorter.',
    href: '/settings/retention',
    icon: Hourglass,
    reads: ['settings'],
    line: fromSettings(retentionLine),
  },
  {
    key: 'sharing',
    group: 'reading',
    title: 'Sharing',
    description: 'How long a new share link works, whether it needs a passcode, and how a sent link leaves.',
    href: '/settings/sharing',
    icon: Share2,
    reads: ['settings'],
    line: fromSettings(sharingLine),
  },
  {
    key: 'parsing',
    group: 'reading',
    title: 'Parsing',
    description: 'Reading a document’s details — only when a person or an app asks. Which types, accepted how, and where AI may run.',
    href: '/settings/parsing',
    icon: ScanText,
    reads: ['settings', 'profiles'],
    line: ({ settings, profiles }) => (settings ? parsingLine(settings, profiles) : null),
  },
  {
    key: 'usage',
    group: 'reading',
    title: 'Parsing usage',
    description: 'What ProDoc read in a month — asked by a person or by an app — and what the AI it needed cost.',
    href: '/settings/usage',
    icon: ChartColumn,
    reads: [],
    line: () => ({ line: 'Read only · one month at a time', tone: 'unset' }),
    // The month's usage is the administrator's (engine C-E11): a reader is not shown the card.
    visible: (can) => can(SETTINGS, 'can_update'),
  },
  {
    key: 'erasure',
    group: 'privacy',
    title: 'Personal-data erasure',
    description: 'Erase a person’s documents on their request (DPDP). What the law still requires is held until its date.',
    href: '/settings/erasure',
    icon: UserX,
    reads: [],
    line: () => ({ line: 'Each request re-verified and recorded', tone: 'unset' }),
    visible: mayErase,
  },
]

/** The cards this person sees, by group, in registry order — an empty group dropped. */
export function visibleCards(can: (resource: string, verb: Verb) => boolean) {
  return HUB_GROUPS.map((group) => ({
    group,
    cards: SETTINGS_CARDS.filter((card) => card.group === group.key && (card.visible ? card.visible(can) : true)),
  })).filter((entry) => entry.cards.length > 0)
}

/** Where an old `/settings#…` lands (D87): each section of the one page before W28, now its own page. */
export const LEGACY_HASHES: Record<string, string> = {
  types: '/settings/types',
  review: '/settings/review',
  retention: '/settings/retention',
  sharing: '/settings/sharing',
  parsing: '/settings/parsing',
  'parsing-usage': '/settings/usage',
  erasure: '/settings/erasure',
}

/** The page an old hash names, or `null` — an unknown hash stays on the hub. */
export function legacyTarget(hash: string): string | null {
  const key = hash.replace(/^#/, '')
  return Object.prototype.hasOwnProperty.call(LEGACY_HASHES, key) ? LEGACY_HASHES[key]! : null
}

/* -------------------------------------------------------------- the hub */

export function SettingsHub() {
  const { client, parsing, can, orgName, consoleUrl } = useSettingsHost()
  const { hash } = useLocation()
  const navigate = useNavigate()
  const settings = useLoad(() => client.settings.get(), [client])
  const profiles = useLoad(() => parsing.profiles(), [parsing])
  const canEdit = can(SETTINGS, 'can_update')

  useEffect(() => {
    const target = legacyTarget(hash)
    if (target) navigate(target, { replace: true })
  }, [hash, navigate])

  const data: HubData = { settings: settings.data, profiles: profiles.data }
  const pending: Record<Read, boolean> = { settings: settings.loading && !settings.data, profiles: profiles.loading && !profiles.data }
  const failed = Boolean(settings.error) && !settings.data

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <PageHeader
        title="Settings"
        description={`How ProDoc files, reviews, keeps and shares documents for ${orgName}.`}
        meta={
          canEdit ? undefined : (
            <Badge tone="neutral" size="sm">
              View only
            </Badge>
          )
        }
      />

      {failed && (
        <Alert tone="warning">
          What is set could not be read: {settings.error} Every page still opens; the line under each card is back once it is read.{' '}
          <Button variant="link" onClick={() => void settings.reload()}>
            Try again
          </Button>
        </Alert>
      )}

      {visibleCards(can).map(({ group, cards }) => (
        <HubGroup key={group.key} id={`settings-${group.key}`} title={group.title} description={group.description} columns={cards.length % 3 === 0 || cards.length === 1 ? 3 : 2}>
          {cards.map((card) => (
            <HubCard
              key={card.key}
              cardKey={card.key}
              href={card.href}
              icon={card.icon}
              title={card.title}
              description={card.description}
              loading={card.reads.some((read) => pending[read])}
              line={card.line(data)}
            />
          ))}
        </HubGroup>
      ))}

      <ConsoleRows consoleUrl={consoleUrl} orgName={orgName} />
    </div>
  )
}

/**
 * FR-37: the prototype's Admin showed these; they are the platform's. Each is
 * a door into the FiveOX console (`repo:fiveox:apps/console/src/router.tsx`:
 * `/profile`, `/members`, `/integrations`), never a copy.
 */
function ConsoleRows({ consoleUrl, orgName }: { consoleUrl: string; orgName: string }) {
  const base = consoleUrl.replace(/\/+$/, '')
  return (
    <section aria-labelledby="settings-fiveox" className="space-y-3">
      <div>
        <h2 id="settings-fiveox" className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          Managed in FiveOX
        </h2>
        <p className="text-muted-foreground text-sm">Not ProDoc’s — each opens the FiveOX console.</p>
      </div>
      <div className="grid gap-3">
        <HubRow external rowKey="organisation" href={`${base}/profile`} icon={Building2} title="Organisation" description={`${orgName}’s name, addresses, GSTIN and PAN.`} />
        <HubRow
          external
          rowKey="access"
          href={`${base}/members`}
          icon={Users}
          title="Users & access"
          description="Who is in the organisation, and the ProDoc role each holds — administrator, uploader, reviewer or viewer."
        />
        <HubRow
          external
          rowKey="messaging"
          href={`${base}/integrations`}
          icon={MessageSquare}
          title="Messaging"
          description="The organisation’s own mail server and WhatsApp number, which a sent link leaves from. ProDoc holds no credential."
        />
      </div>
    </section>
  )
}
